import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Space,
  Tooltip,
  message,
  Empty,
  Segmented,
  Tag,
  Modal,
  Input,
  Select,
  DatePicker,
  Spin,
} from "antd";
import ImageThumbnail from "./ImageThumbnail";
import {
  DownloadOutlined,
  DeleteOutlined,
  EyeOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FileTextOutlined,
  LoadingOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  SearchOutlined,
  FilterOutlined,
  ClearOutlined,
  PrinterOutlined,
  ShareAltOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { formatDate } from "../services/api.service";
import { Document, Page, pdfjs } from "react-pdf";
import logger from "../utils/logger";
import "../styles/DocumentsList.css";

// Setup PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const DocumentsList = ({
  documents = [],
  onDelete,
  onDownload,
  onUpload,
  onRefresh,
  loading = false,
  caseId,
  clientId,
}) => {
  const [filePreviewURLs, setFilePreviewURLs] = useState({});
  const [imgError, setImgError] = useState({});
  const [viewMode, setViewMode] = useState("grid");

  // Unified state for the preview modal
  const [previewState, setPreviewState] = useState({
    visible: false,
    document: null,
  });

  // State for PDF viewer
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);

  // State for document preview
  const [previewLoading, setPreviewLoading] = useState(false);
  const [contentUrl, setContentUrl] = useState(null);
  const [previewError, setPreviewError] = useState(null);

  // Filter and search states
  const [searchText, setSearchText] = useState("");
  const [selectedFileType, setSelectedFileType] = useState("");
  const [selectedMimeType, setSelectedMimeType] = useState("");
  const [filteredDocuments, setFilteredDocuments] = useState([]);

  const documentsList = Array.isArray(documents) ? documents : [];

  useEffect(() => {
    let filtered = [...documentsList];
    if (searchText) {
      filtered = filtered.filter(
        (doc) =>
          doc.title?.toLowerCase().includes(searchText.toLowerCase()) ||
          doc.description?.toLowerCase().includes(searchText.toLowerCase()) ||
          doc.original_name?.toLowerCase().includes(searchText.toLowerCase())
      );
    }
    if (selectedFileType) {
      filtered = filtered.filter((doc) =>
        doc.document_type
          ?.toLowerCase()
          .includes(selectedFileType.toLowerCase())
      );
    }
    if (selectedMimeType) {
      filtered = filtered.filter((doc) =>
        doc.mime_type?.toLowerCase().includes(selectedMimeType.toLowerCase())
      );
    }
    setFilteredDocuments(filtered);
  }, [documentsList, searchText, selectedFileType, selectedMimeType]);

  // تحميل المحتوى عند فتح المودال
  useEffect(() => {
    if (previewState.visible && previewState.document) {
      setPreviewLoading(true);
      setPreviewError(null);

      const baseURL = process.env.REACT_APP_API_URL || "http://localhost:5000";
      const token =
        localStorage.getItem("token") || sessionStorage.getItem("token");
      const previewUrl = `${baseURL}/documents/${previewState.document.id}/preview`;

      const headers = {};
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      fetch(previewUrl, { headers })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`فشل في تحميل المستند: ${response.status}`);
          }
          return response.blob();
        })
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          setContentUrl(url);
          setPreviewLoading(false);
        })
        .catch((err) => {
          logger.error("Error loading document:", err);
          setPreviewError(err.message);
          setPreviewLoading(false);
        });
    }
  }, [previewState.visible, previewState.document]);

  // تنظيف URL عند إغلاق المودال
  useEffect(() => {
    if (!previewState.visible && contentUrl) {
      setTimeout(() => {
        URL.revokeObjectURL(contentUrl);
        setContentUrl(null);
      }, 1000); // تأخير بسيط قبل إلغاء URL
    }
  }, [previewState.visible, contentUrl]);

  const uniqueFileTypes = [
    ...new Set(
      documentsList
        .map((doc) => doc.document_type?.split("/")[1]?.toUpperCase())
        .filter(Boolean)
    ),
  ];

  const uniqueMimeTypes = [
    ...new Set(documentsList.map((doc) => doc.mime_type).filter(Boolean)),
  ];

  const clearFilters = () => {
    setSearchText("");
    setSelectedFileType("");
    setSelectedMimeType("");
  };

  useEffect(() => {
    const baseURL = process.env.REACT_APP_API_URL || "http://localhost:5000";
    const imageDocuments = documentsList.filter((doc) =>
      doc.document_type?.toLowerCase().includes("image")
    );
    const previewUrls = {};
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");
    imageDocuments.forEach((doc) => {
      // استخدام baseURL الصحيح الذي يحتوي بالفعل على /api
      // إضافة التوكن كمعامل مع ترميز URL صحيح
      previewUrls[doc.id] = `${baseURL}/documents/${
        doc.id
      }/preview?thumbnail=true&token=${encodeURIComponent(token)}`;
    });
    setFilePreviewURLs(previewUrls);
    setImgError({});
  }, [documentsList]);

  const getFileIcon = (fileType) => {
    if (!fileType) return <FileOutlined style={{ fontSize: "inherit" }} />;
    const type = fileType?.toLowerCase() || "";
    if (type.includes("excel") || type.includes("sheet")) {
      return (
        <FileExcelOutlined style={{ color: "#52C41A", fontSize: "40px" }} />
      );
    }
    if (type.includes("pdf")) {
      return (
        <FilePdfOutlined style={{ color: "#FF4D4F", fontSize: "inherit" }} />
      );
    }
    if (type.includes("image")) {
      return (
        <FileImageOutlined style={{ color: "#36CFC9", fontSize: "inherit" }} />
      );
    }
    if (type.includes("word") || type.includes("doc")) {
      return (
        <FileWordOutlined style={{ color: "#2F54EB", fontSize: "inherit" }} />
      );
    }
    if (type.includes("text")) {
      return (
        <FileTextOutlined style={{ color: "#722ED1", fontSize: "inherit" }} />
      );
    }
    return <FileOutlined style={{ fontSize: "inherit" }} />;
  };

  const getFileTagProps = (fileType) => {
    if (!fileType) return { color: "default", text: "ملف" };
    const type = fileType?.toLowerCase() || "";
    if (type.includes("excel") || type.includes("sheet"))
      return { color: "green", text: "Excel" };
    if (type.includes("pdf")) return { color: "red", text: "PDF" };
    if (type.includes("image")) return { color: "cyan", text: "صورة" };
    if (type.includes("word") || type.includes("doc"))
      return { color: "blue", text: "Word" };
    if (type.includes("text")) return { color: "purple", text: "نص" };
    return { color: "default", text: "ملف" };
  };

  // --- REFACTORED PREVIEW LOGIC ---
  const handlePreview = (document) => {
    if (!document) return;
    setPageNumber(1); // Reset page number for new document
    setNumPages(null);
    setPreviewState({ visible: true, document: document });
  };

  const closePreviewModal = () => {
    setPreviewState({ visible: false, document: null });
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const goToPrevPage = () => setPageNumber(pageNumber - 1);
  const goToNextPage = () => setPageNumber(pageNumber + 1);
  // --- END REFACTORED PREVIEW LOGIC ---

  const handleDelete = (document) => {
    Modal.confirm({
      title: "تأكيد حذف المستند",
      content: `هل أنت متأكد من حذف المستند \"${document.title}\"? لا يمكن التراجع عن هذا الإجراء.`,
      okText: "حذف",
      cancelText: "إلغاء",
      okType: "danger",
      centered: true,
      direction: "rtl",
      className: "rtl-modal",
      onOk: async () => {
        try {
          await onDelete(document);
          message.success("تم حذف المستند بنجاح");
        } catch (error) {
          message.error("فشل في حذف المستند");
        }
      },
    });
  };

  const handleDownload = (document) => {
    onDownload(document);
  };

  const isPrintable = (document) => {
    const type = document.document_type?.toLowerCase() || "";
    return (
      type.includes("pdf") ||
      type.includes("image") ||
      type.includes("text") ||
      type.includes("html")
    );
  };

  const handlePrint = (document) => {
    if (!document) return;

    if (!isPrintable(document)) {
      message.warning(
        "الطباعة غير مدعومة لهذا النوع من الملفات. يرجى تحميل الملف لطباعته."
      );
      return;
    }
    const baseURL = process.env.REACT_APP_API_URL || "http://localhost:5000";
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");
    const printUrl = `${baseURL}/documents/${document.id}/print?token=${token}`;
    const printWindow = window.open(printUrl, "_blank", "width=800,height=600");
    if (!printWindow) {
      message.error("فشل في فتح نافذة الطباعة، يرجى التحقق من إعدادات المتصفح");
    }
  };

  const handleShare = async (document) => {
    if (!document) return;

    try {
      message.loading({ content: "جاري إنشاء رابط المشاركة...", key: "share" });
      
      // طلب رابط مشاركة آمن من الخادم
      // نفترض أن apiService تم استيراده كـ default import في الملف، لكن إذا لم يكن كذلك سنحتاج لتعديل الاستيراد
      // في الكود الحالي، يبدو أنه يتم استيراد formatDate فقط من api.service
      // لذا سنقوم باستخدام fetch مباشرة أو نحتاج لاستيراد apiService
      
      const baseURL = process.env.REACT_APP_API_URL || "http://localhost:5000";
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      
      const response = await fetch(`${baseURL}/documents/${document.id}/share`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('فشل في إنشاء رابط المشاركة');
      }

      const data = await response.json();
      const shareUrl = data.data.shareUrl;

      message.success({ content: "تم إنشاء الرابط بنجاح", key: "share" });

      if (navigator.share) {
        navigator
          .share({
            title: document.title,
            text: `مستند: ${document.title}`,
            url: shareUrl,
          })
          .catch((error) => {
            logger.debug("Error sharing:", error);
            fallbackShare(shareUrl, document.title);
          });
      } else {
        fallbackShare(shareUrl, document.title);
      }
    } catch (error) {
      logger.error("Share error:", error);
      message.error({ content: "فشل في مشاركة المستند", key: "share" });
    }
  };

  const fallbackShare = (url, title) => {
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(url)
        .then(() => message.success("تم نسخ رابط المشاركة الآمن إلى الحافظة"))
        .catch(() => showShareOptions(url, title));
    } else {
      showShareOptions(url, title);
    }
  };

  const showShareOptions = (url, title) => {
    Modal.info({
      title: "مشاركة المستند",
      content: (
        <div style={{ direction: "rtl" }}>
          <p>يمكنك مشاركة هذا المستند عبر:</p>
          <div style={{ marginTop: "16px" }}>
            <Input.TextArea
              value={url}
              readOnly
              rows={3}
              style={{ marginBottom: "12px" }}
            />
            <Button
              type="primary"
              block
              onClick={() => {
                navigator.clipboard.writeText(url);
                message.success("تم نسخ الرابط");
              }}
            >
              نسخ الرابط
            </Button>
          </div>
        </div>
      ),
      className: "rtl-modal",
      okText: "إغلاق",
    });
  };

  const handleImageError = (docId) => {
    setImgError((prev) => ({ ...prev, [docId]: true }));

    // محاولة تحديث رابط الصورة بدون التوكن
    const baseURL = process.env.REACT_APP_API_URL || "http://localhost:5000";
    setFilePreviewURLs((prev) => ({
      ...prev,
      [docId]: `${baseURL}/documents/${docId}/preview?thumbnail=true`,
    }));
  };

  const columns = [
    {
      title: "اسم المستند",
      dataIndex: "title",
      key: "title",
      width: 250,
      sorter: (a, b) => a.title?.localeCompare(b.title, "ar"),
      sortDirections: ["ascend", "descend"],
      render: (text, record) => (
        <div className="document-info" style={{ direction: "rtl" }}>
          <div
            style={{
              fontWeight: "bold",
              marginBottom: "4px",
              wordWrap: "break-word",
              whiteSpace: "normal",
              lineHeight: "1.4",
            }}
          >
            {text}
          </div>
          {record.description && (
            <div
              style={{
                fontSize: "12px",
                color: "#666",
                marginTop: "2px",
                wordWrap: "break-word",
                whiteSpace: "normal",
                lineHeight: "1.3",
              }}
            >
              {record.description}
            </div>
          )}
          {record.original_name && record.original_name !== text && (
            <div
              style={{
                fontSize: "11px",
                color: "#999",
                marginTop: "2px",
                fontStyle: "italic",
                wordWrap: "break-word",
                whiteSpace: "normal",
              }}
            >
              📄 {record.original_name}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "نوع الملف",
      dataIndex: "document_type",
      key: "document_type",
      width: 140,
      sorter: (a, b) => a.document_type?.localeCompare(b.document_type),
      sortDirections: ["ascend", "descend"],
      render: (text, record) => {
        const { color, text: tagText } = getFileTagProps(text);
        return (
          <div style={{ direction: "rtl" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "4px",
              }}
            >
              <div style={{ fontSize: "20px", marginLeft: "8px" }}>
                {getFileIcon(text)}
              </div>
              <Tag color={color}>{tagText}</Tag>
            </div>
            {record.file_size && (
              <div
                style={{ fontSize: "11px", color: "#999", marginTop: "2px" }}
              >
                📁 {(record.file_size / 1024).toFixed(1)} KB
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "تصنيف الملف",
      dataIndex: "mime_type",
      key: "mime_type",
      width: 120,
      sorter: (a, b) =>
        (a.mime_type || "").localeCompare(b.mime_type || "", "ar"),
      sortDirections: ["ascend", "descend"],
      render: (text) => (
        <div style={{ direction: "rtl" }}>
          {text ? (
            <Tag color="blue">{text}</Tag>
          ) : (
            <Tag color="default">غير محدد</Tag>
          )}
        </div>
      ),
    },
    {
      title: "القضية المرتبطة",
      key: "case_info",
      width: 150,
      render: (_, record) => (
        <div style={{ direction: "rtl" }}>
          {record.case_number ? (
            <div>
              <Tag color="blue" style={{ marginBottom: "2px" }}>
                {record.case_number}
              </Tag>
              {record.case_title && (
                <div style={{ fontSize: "11px", color: "#666" }}>
                  {record.case_title}
                </div>
              )}
            </div>
          ) : (
            <Tag color="default">غير مرتبط بقضية</Tag>
          )}
        </div>
      ),
    },
    {
      title: "معلومات الرفع",
      key: "upload_info",
      width: 150,
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      sortDirections: ["ascend", "descend"],
      defaultSortOrder: "descend",
      render: (_, record) => (
        <div style={{ direction: "rtl" }}>
          <div style={{ fontSize: "12px", marginBottom: "2px" }}>
            📅 {formatDate(record.created_at)}
          </div>
          {record.uploaded_by && (
            <div style={{ fontSize: "11px", color: "#666" }}>
              👤 {record.uploaded_by}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "الإجراءات",
      key: "actions",
      width: 220,
      align: "center",
      render: (_, record) => (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: "2px",
          }}
        >
          <Tooltip title="معاينة">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handlePreview(record)}
              style={{ minWidth: "32px" }}
            />
          </Tooltip>
          <Tooltip title="طباعة">
            <Button
              type="text"
              size="small"
              icon={<PrinterOutlined />}
              onClick={() => handlePrint(record)}
              style={{ color: "#722ED1", minWidth: "32px" }}
            />
          </Tooltip>
          <Tooltip title="مشاركة">
            <Button
              type="text"
              size="small"
              icon={<ShareAltOutlined />}
              onClick={() => handleShare(record)}
              style={{ color: "#13C2C2", minWidth: "32px" }}
            />
          </Tooltip>
          <Tooltip title="تحميل">
            <Button
              type="text"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => handleDownload(record)}
              style={{ color: "#52C41A", minWidth: "32px" }}
            />
          </Tooltip>
          <Tooltip title="حذف">
            <Button
              type="text"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
              style={{ minWidth: "32px" }}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  const renderGridView = () => {
    return (
      <div className="documentsGrid" style={{ direction: "rtl" }}>
        {filteredDocuments.map((document) => (
          <div
            key={document.id}
            className="fileCard"
            style={{ direction: "rtl" }}
          >
            <div
              className="fileTag"
              style={{
                display: "flex",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "4px",
              }}
            >
              <Tag color={getFileTagProps(document.document_type).color}>
                {getFileTagProps(document.document_type).text}
              </Tag>
              {document.case_number && (
                <Tag color="blue" size="small">
                  {document.case_number}
                </Tag>
              )}
            </div>

            <div
              className="filePreview"
              style={{ position: "relative", cursor: "pointer" }}
              onClick={() => handlePreview(document)}
            >
              {document.document_type?.toLowerCase().includes("image") &&
              !imgError[document.id] ? (
                <div
                  className="imagePreview"
                  style={{
                    position: "relative",
                    width: "100%",
                    height: "120px",
                  }}
                >
                  <ImageThumbnail
                    src={filePreviewURLs[document.id]}
                    alt={document.title}
                    documentType={document.document_type}
                    onError={() => handleImageError(document.id)}
                    onClick={() => handlePreview(document)}
                  />
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background:
                        "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.7) 100%)",
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "flex-end",
                      justifyContent: "center",
                      padding: "10px",
                      opacity: 0,
                      transition: "opacity 0.3s ease",
                    }}
                    className="image-overlay"
                    onMouseEnter={(e) => (e.target.style.opacity = "1")}
                    onMouseLeave={(e) => (e.target.style.opacity = "0")}
                  >
                    <div style={{ color: "white", textAlign: "center" }}>
                      <EyeOutlined
                        style={{ fontSize: "20px", marginBottom: "4px" }}
                      />
                      <div style={{ fontSize: "12px" }}>معاينة</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="fileIconWrapper" style={{ cursor: "pointer" }}>
                  {getFileIcon(document.document_type)}
                </div>
              )}
            </div>

            <div
              className="fileInfo"
              style={{ direction: "rtl", textAlign: "right" }}
            >
              <div
                className="fileName"
                title={document.title}
                style={{
                  fontWeight: "bold",
                  marginBottom: "4px",
                  fontSize: "14px",
                }}
              >
                {document.title}
              </div>

              {document.description && (
                <div
                  style={{
                    fontSize: "11px",
                    color: "#666",
                    marginBottom: "4px",
                    lineHeight: "1.3",
                  }}
                >
                  {document.description}
                </div>
              )}

              {document.mime_type && (
                <div
                  style={{
                    fontSize: "11px",
                    color: "#1890ff",
                    marginBottom: "4px",
                    fontWeight: "bold",
                  }}
                >
                  🏷️ {document.mime_type}
                </div>
              )}

              {document.case_title && (
                <div
                  style={{
                    fontSize: "11px",
                    color: "#1890ff",
                    marginBottom: "4px",
                    fontStyle: "italic",
                  }}
                >
                  📋 {document.case_title}
                </div>
              )}

              <div
                style={{
                  fontSize: "11px",
                  color: "#999",
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: "8px",
                }}
              >
                <span>📅 {formatDate(document.created_at)}</span>
                {document.file_size && (
                  <span>📁 {(document.file_size / 1024).toFixed(1)} KB</span>
                )}
              </div>

              {document.uploaded_by && (
                <div
                  style={{
                    fontSize: "10px",
                    color: "#999",
                    marginTop: "2px",
                  }}
                >
                  👤 {document.uploaded_by}
                </div>
              )}
            </div>

            <div
              className="fileActions"
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "4px",
                flexWrap: "wrap",
              }}
            >
              <Tooltip title="معاينة">
                <Button
                  type="text"
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => handlePreview(document)}
                />
              </Tooltip>
              <Tooltip title="طباعة">
                <Button
                  type="text"
                  size="small"
                  icon={<PrinterOutlined />}
                  onClick={() => handlePrint(document)}
                  style={{ color: "#722ED1" }}
                />
              </Tooltip>
              <Tooltip title="مشاركة">
                <Button
                  type="text"
                  size="small"
                  icon={<ShareAltOutlined />}
                  onClick={() => handleShare(document)}
                  style={{ color: "#13C2C2" }}
                />
              </Tooltip>
              <Tooltip title="تحميل">
                <Button
                  type="text"
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={() => handleDownload(document)}
                  style={{ color: "#52C41A" }}
                />
              </Tooltip>
              <Tooltip title="حذف">
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(document)}
                />
              </Tooltip>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // --- NEW UNIFIED PREVIEW MODAL ---
  const renderPreviewModal = () => {
    const { visible, document } = previewState;
    if (!visible || !document) {
      return null;
    }

    const baseURL = process.env.REACT_APP_API_URL || "http://localhost:5000";
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");
    const previewUrl = `${baseURL}/documents/${document.id}/preview`;
    const fileType = document.document_type?.toLowerCase() || "";

    const isImage = fileType.includes("image");
    const isPdf = fileType.includes("pdf");
    const isText =
      fileType.includes("text") ||
      fileType.includes("json") ||
      fileType.includes("xml") ||
      fileType.includes("javascript") ||
      fileType.includes("css") ||
      fileType.includes("html");

    // استخدام الحالات التي تم تعريفها في مستوى المكون

    let content;

    if (previewLoading) {
      content = (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <Spin size="large" tip="جاري تحميل المستند..." />
        </div>
      );
    } else if (previewError) {
      content = (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <Empty
            image={<FileOutlined style={{ fontSize: 60 }} />}
            description={
              <div>
                <p>حدث خطأ أثناء تحميل المستند:</p>
                <p style={{ color: "#ff4d4f" }}>{previewError}</p>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={() => handleDownload(document)}
                  style={{ marginTop: 16 }}
                >
                  تنزيل الملف
                </Button>
              </div>
            }
          />
        </div>
      );
    } else if (isImage && contentUrl) {
      content = (
        <img
          src={contentUrl}
          alt={document.title}
          onLoad={() => logger.debug("Image loaded successfully")}
          onError={(e) => {
            logger.error("Error loading image:", e);
            setPreviewError("فشل في تحميل الصورة");
          }}
          style={{
            maxWidth: "100%",
            maxHeight: "80vh",
            objectFit: "contain",
            margin: "0 auto",
            display: "block",
          }}
        />
      );
    } else if (isPdf && contentUrl) {
      content = (
        <div
          className="pdf-container"
          style={{ textAlign: "center", padding: "20px" }}
        >
          <div style={{ marginBottom: "20px" }}>
            <Button
              type="primary"
              icon={<FilePdfOutlined />}
              onClick={() => {
                try {
                  window.open(contentUrl, "_blank");
                } catch (e) {
                  logger.error("Error opening PDF:", e);
                  setPreviewError("فشل في فتح ملف PDF");
                }
              }}
              style={{ fontSize: "16px", height: "40px" }}
            >
              فتح ملف PDF في نافذة جديدة
            </Button>
          </div>
          <p style={{ color: "#666", marginTop: "20px" }}>
            لمعاينة ملفات PDF بشكل أفضل، يرجى فتحها في نافذة جديدة
          </p>
          {numPages && (
            <div
              className="pdf-navigation"
              style={{ textAlign: "center", marginTop: "1rem" }}
            >
              <Button onClick={goToPrevPage} disabled={pageNumber <= 1}>
                <RightOutlined /> السابق
              </Button>
              <span style={{ margin: "0 1rem" }}>
                صفحة {pageNumber} من {numPages}
              </span>
              <Button onClick={goToNextPage} disabled={pageNumber >= numPages}>
                التالي <LeftOutlined />
              </Button>
            </div>
          )}
        </div>
      );
    } else if (isText && contentUrl) {
      content = (
        <div
          className="text-preview-container"
          style={{ maxHeight: "70vh", overflow: "auto" }}
        >
          <iframe
            src={contentUrl}
            title={document.title}
            onLoad={() => logger.debug("Text file loaded successfully")}
            onError={(e) => {
              logger.error("Error loading text file:", e);
              setPreviewError("فشل في تحميل الملف النصي");
            }}
            style={{
              width: "100%",
              height: "70vh",
              border: "1px solid #eee",
              borderRadius: "4px",
            }}
          />
        </div>
      );
    } else {
      content = (
        <Empty
          image={<FileOutlined style={{ fontSize: 60 }} />}
          description={
            <div>
              <p>المعاينة غير متاحة لهذا النوع من الملفات.</p>
              <p>({document.title})</p>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={() => handleDownload(document)}
                style={{ marginTop: 16 }}
              >
                تنزيل الملف
              </Button>
            </div>
          }
        />
      );
    }

    return (
      <Modal
        title={
          <div style={{ textAlign: "right", direction: "rtl" }}>
            <EyeOutlined style={{ marginLeft: "8px" }} />
            معاينة المستند: {document.title}
          </div>
        }
        open={visible}
        onCancel={closePreviewModal}
        footer={null}
        width="90%"
        style={{ maxWidth: "1000px", direction: "rtl" }}
        centered
        destroyOnClose
        className="rtl-modal preview-modal"
      >
        <div className="preview-modal-body">{content}</div>
      </Modal>
    );
  };

  return (
    <div className="documents-list-container">
      {/* شريط التحكم والتصفية */}
      <div className="documents-controls" style={{ marginBottom: "16px" }}>
        <div className="flex justify-between items-center mb-4">
          <Segmented
            options={[
              {
                value: "grid",
                icon: <AppstoreOutlined />,
                label: "بطاقات",
              },
              {
                value: "list",
                icon: <UnorderedListOutlined />,
                label: "قائمة",
              },
            ]}
            value={viewMode}
            onChange={setViewMode}
          />

          <div
            className="filter-controls"
            style={{ display: "flex", gap: "8px", alignItems: "center" }}
          >
            <Button
              icon={<ClearOutlined />}
              onClick={clearFilters}
              disabled={!searchText && !selectedFileType && !selectedMimeType}
            >
              مسح التصفية
            </Button>
          </div>
        </div>

        <div
          className="search-filter-bar"
          style={{
            display: "flex",
            gap: "12px",
            marginBottom: "16px",
            direction: "rtl",
            flexWrap: "wrap",
          }}
        >
          <Input
            placeholder="البحث في المستندات..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{
              width: "250px",
              direction: "rtl",
            }}
            allowClear
          />

          <Select
            placeholder="نوع الملف"
            value={selectedFileType}
            onChange={setSelectedFileType}
            style={{ width: "150px" }}
            allowClear
            suffixIcon={<FilterOutlined />}
          >
            {uniqueFileTypes.map((type) => (
              <Select.Option key={type} value={type.toLowerCase()}>
                {type}
              </Select.Option>
            ))}
          </Select>

          <Select
            placeholder="تصنيف الملف"
            value={selectedMimeType}
            onChange={setSelectedMimeType}
            style={{ width: "150px" }}
            allowClear
            suffixIcon={<FilterOutlined />}
          >
            {uniqueMimeTypes.map((type) => (
              <Select.Option key={type} value={type}>
                {type}
              </Select.Option>
            ))}
          </Select>

          <div
            style={{
              color: "#666",
              fontSize: "14px",
              alignSelf: "center",
              marginRight: "auto",
            }}
          >
            {filteredDocuments.length} من أصل {documentsList.length} مستند
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <LoadingOutlined style={{ fontSize: 24 }} spin />
          <span className="mr-2">جاري تحميل المستندات...</span>
        </div>
      ) : filteredDocuments.length === 0 && documentsList.length > 0 ? (
        <Empty
          description="لا توجد نتائج للبحث أو التصفية المحددة"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : filteredDocuments.length === 0 ? (
        <Empty
          description="لا توجد مستندات"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : viewMode === "grid" ? (
        renderGridView()
      ) : (
        <div style={{ direction: "rtl" }}>
          <Table
            columns={columns}
            dataSource={filteredDocuments}
            rowKey="id"
            scroll={{ x: 1000 }}
            size="middle"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50"],
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} من ${total} مستند`,
            }}
            locale={{
              emptyText: "لا توجد مستندات تطابق معايير البحث",
              filterEmptyText: "لا توجد خيارات",
              filterSearchPlaceholder: "بحث...",
              selectAll: "تحديد الكل",
              selectInvert: "عكس التحديد",
              sortTitle: "ترتيب",
            }}
          />
        </div>
      )}

      {renderPreviewModal()}
    </div>
  );
};

export default DocumentsList;
